import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Category bar
// Props:
//   selected, onSelect, categories (optional — falls back to fetching /api/categories itself)
//   productsRef  - ref to the Products section (used to know when hide-on-scroll may activate)
//   headerHeight - current header height in px (so this bar sticks seamlessly below it)
export default function CategoryBar({ selected, onSelect, categories: catProp, productsRef, headerHeight = 112 }) {
  const [hidden, setHidden] = useState(false);
  const [cats, setCats] = useState(catProp || []);
  const lastY = useRef(0);
  const barRef = useRef(null);

  useEffect(() => {
    if (catProp && catProp.length) { setCats(catProp); return; }
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, [catProp]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const productsTop = productsRef?.current?.offsetTop ?? Infinity;
      // Only allow hiding once we've entered the products section.
      const inProducts = y + headerHeight + 60 >= productsTop;
      if (!inProducts) {
        setHidden(false);
        lastY.current = y;
        return;
      }
      if (y > lastY.current + 6) setHidden(true);
      else if (y < lastY.current - 6) setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [productsRef, headerHeight]);

  useEffect(() => {
    const el = barRef.current?.querySelector(`[data-cat="${selected}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [selected]);

  if (!cats || cats.length === 0) return null;

  // Wide-cell heuristic for legacy responsive grid — apply to the last item
  // when its name is longer than the others so the mobile 4-col grid still
  // wraps cleanly. Pure UI polish, no behavior change.
  return (
    <div
      data-testid="category-bar"
      className={`category-bar sticky z-30 bg-brown-border/25 border-y border-brown-border/60 shadow-[inset_0_-1px_0_rgba(59,43,32,0.06)] ${hidden ? "is-hidden" : ""}`}
      style={{ top: `${headerHeight}px` }}
    >
      <div
        ref={barRef}
        className="max-w-7xl mx-auto px-4 md:px-6 py-3.5 md:py-4 grid grid-cols-4 gap-2 md:flex md:items-center md:gap-2.5 md:overflow-x-auto md:no-scrollbar md:smooth-scroll"
      >
        {cats.map((c) => {
          const active = c.slug === selected;
          const wide = (c.name || "").length > 10;
          return (
            <button
              key={c.slug}
              data-cat={c.slug}
              data-testid={`cat-pill-${c.slug}`}
              onClick={() => onSelect(c.slug)}
              className={`min-w-0 md:flex-shrink-0 px-3 md:px-5 py-2 rounded-full text-[11px] md:text-sm font-semibold tracking-wide transition-all duration-300 border ${wide ? "col-span-2 md:col-span-1" : ""} ${
                active
                  ? "bg-brand-red text-ivory border-brand-red shadow-[0_2px_8px_-2px_rgba(200,42,42,0.4)]"
                  : "bg-ivory text-brown border-brown-medium/50 hover:border-brown-medium hover:bg-white"
              }`}
            >
              {c.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
